import { Button, Stack, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import useDialog from '../hooks/useDialog';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';
import { processBrandWithdrawalRequest } from '../fetchFns';

interface ProcessBrandWithdrawalRequestDialogProps {
  withdrawalRequestId: number;
  newStatus: 'completed' | 'rejected';
  /** Re-fetch the list after the request is processed. */
  getData: () => void;
}

type FormValues = {
  remarks?: string;
};

const defaultValues: FormValues = {
  remarks: '',
};

export default function ProcessBrandWithdrawalRequestDialog({
  withdrawalRequestId,
  newStatus,
  getData,
}: ProcessBrandWithdrawalRequestDialogProps) {
  const backdrop = useBackdrop();
  const dialog = useDialog();
  const snackbar = useSnackbar();
  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
  });

  const handleProcess = async (data: FormValues) => {
    backdrop.handleBackdropOpen();
    try {
      await processBrandWithdrawalRequest(
        withdrawalRequestId,
        newStatus,
        data.remarks,
      );
      reset();
      dialog.handleDialogClose();
      getData();
      snackbar.handleSnackbarOpen(
        `The withdrawal request has been ${
          newStatus === 'completed' ? 'approved' : 'rejected'
        }.`,
        'success',
      );
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast.stack);
      snackbar.handleSnackbarOpen(errorCast.message, 'error');
    }
    backdrop.handleBackdropClose();
  };

  return (
    <Stack
      component="form"
      direction="column"
      spacing={2}
      onSubmit={handleSubmit(handleProcess)}>
      <Controller
        name="remarks"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Remarks"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            fullWidth
          />
        )}
      />
      <Stack justifyContent="end" direction="row" spacing={2}>
        <Button
          onClick={() => {
            dialog.handleDialogClose();
          }}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          color={newStatus === 'completed' ? 'primary' : 'error'}>
          {newStatus === 'completed' ? 'Approve' : 'Reject'}
        </Button>
      </Stack>
    </Stack>
  );
}
